<template>
  <b-card-text>
    <div class="card">
      <div class="card-body">
        <div>
          <h4 class="card-title">{{ $t('pages.settings.marketplace_settings.title') }}</h4>
          <p class="card-title-desc">
            <i class="mdi mdi-information-outline"></i>
            {{ $t('pages.settings.marketplace_settings.subtitle') }}!
          </p>
          <div>
            <div class="mb-3">
              <b-btn class="v-step1" @click="connectAmazonAccount" variant="success">
                <i class="mdi mdi-connection"></i>
                {{ $t('pages.settings.marketplace_settings.connect_btn') }}
              </b-btn>
            </div>
            <div class="text-end">
              <remaining class="mt-2" :used="used" :total="total"></remaining>
            </div>
            <b-tabs pills v-model="region">
              <template v-for="(region, index) in regionList">
                <b-tab :key="index" :title="setTabName(region)" :active="index === 0">
                  <div>
                    <b-card-text>
                      <b-alert show variant="info">
                        <i class="mdi mdi-bullseye-arrow me-2"></i>
                        {{ region.marketplaces }}
                      </b-alert>
                    </b-card-text>
                    <div class="text-center" v-if="region.loading">
                      <lottie :options="loadingAnimation" :height="220" :width="280"></lottie>
                      <p class="text-muted">{{ $t('pages.settings.marketplace_settings.connection_loading') }}</p>
                    </div>
                    <market-place-settings-detail :region="region"
                                                  v-if="region.data.length > 0"
                                                  @updateStatus="updateMarketplaceStatus"
                                                  @sellerEmailUpdated="updateRegionEmail"
                                                  @updateList="updateMarketplaceList"
                    >
                    </market-place-settings-detail>
                  </div>
                </b-tab>
              </template>
            </b-tabs>
          </div>
        </div>
      </div>
    </div>
    <connect-market-place-modal :region="this.regions[this.currentTab]"
                                :disabledRegions="disabledRegions"
                                @close="handleModalClose"
                                @loginStarted="popupFix"/>
    <v-tour name="marketplaceTour" :steps="steps" :options="tourOptions" :callbacks="tourCallbacks"></v-tour>
  </b-card-text>
</template>

<script>
import AuthService from "../../../Services/Api/AuthService";
import MarketPlaceSettingsDetail from "./MarketPlaceSettingsDetail";
import UserMarketPlaceService from "../../../Services/Api/UserMarketPlaceService";
import ConnectMarketPlaceModal from "./ConnectMarketPlaceModal";
import Swal from "sweetalert2";
import Lottie from "vue-lottie";
import loading from '@/assets/animations/animation.js'
import * as ls from "local-storage";
import Remaining from "../../../components/Remaining";
import QuotaMixin from "../../../mixins/QuotaMixin";
import NewMarketplaceTourMixin from "./NewMarketplaceTourMixin";
import UserActivityService from "../../../Services/Api/UserActivityService";

export default {
  name: "MarketPlaces",
  components: {Remaining, ConnectMarketPlaceModal, MarketPlaceSettingsDetail, Lottie},
  mixins: [QuotaMixin, NewMarketplaceTourMixin],
  data() {
    return {
      loadingAnimation: {animationData: loading, loop: true},
      regions: {
        na: {
          name: this.$t('pages.settings.marketplace_settings.regions.na.text'),
          value: "na",
          marketplaces: this.$t('pages.settings.marketplace_settings.regions.na.marketplaces'),
          data: [],
          loading: false
        },
        eu: {
          name: this.$t('pages.settings.marketplace_settings.regions.eu.text'),
          value: "eu",
          marketplaces: this.$t('pages.settings.marketplace_settings.regions.eu.marketplaces'),
          data: [],
          loading: false
        },
        fe: {
          name: this.$t('pages.settings.marketplace_settings.regions.fe.text'),
          value: "fe",
          marketplaces: this.$t('pages.settings.marketplace_settings.regions.fe.marketplaces'),
          data: [],
          loading: false
        },
      },
      region: null,
      amazonKey: null,
      disabledRegions: [],
      user: null,
      selectedRegion: null,
      quotaType: 'marketplace',
      newWindow: null
    }
  },
  computed: {
    regionList() {
      return Object.values(this.regions)
    },
    currentTab() {
      if (this.region === 0) {
        return 'na'
      } else if (this.region === 1) {
        return 'eu'
      }
      return 'fe'
    }
  },
  methods: {
    async handleLwa(payload) {
      this.setLoading(this.selectedRegion, true)
      await AuthService.oauthRedirect(payload).catch((e) => {
        const message = e.data?.message
        let error

        if (message === 'seller_id_registered') {
          error = this.$t('pages.settings.marketplace_settings.errors.seller_id_registered')
        } else if (message === 'oauth_error') {
          error = this.$t('pages.settings.marketplace_settings.errors.auth_failed')
        } else {
          error = message
        }

        UserActivityService.log('User got error connecting marketplace.', {
          error
        })

        this.showMarketplaceError(this.$t('pages.settings.marketplace_settings.errors.lwa_redirect.title'),
            error,
            'error')
        this.setLoading(this.selectedRegion, false)
      })
    },
    updateMarketplaceStatus(e) {
      this.regions[e.region].data.map(marketplace => {
        if (marketplace.id === e.id) {
          marketplace.status = e.status
        }
      })
    },
    async connectAmazonAccount() {
      await this.$bvModal.show('connect-marketplace-modal')
      await UserActivityService.log('User opened connect marketplace modal.')
    },
    setLoading(region, value) {
      if (value) {
        ls.set('marketplace_loading', {region: region, date: +new Date()})
        this.setRegion(region)
      } else {
        ls.remove('marketplace_loading')
      }
      this.regions[region].loading = value
    },
    setTabName(marketplace) {
      if (marketplace.data.length > 0) {
        return `${marketplace.name} (${marketplace.data.length})`
      }
      return marketplace.name
    },
    async updateMarketplaceList(e) {
      await UserMarketPlaceService.updateMarketplaceList({
        payload: e
      })
    },
    handleModalClose() {
      // ls.remove('userJustArrived')
    },
    showMarketplaceError(title, text, icon = 'error') {
      Swal.fire({
        icon,
        title,
        text,
      })
    },
    async updateRegionEmail(e) {
      await UserMarketPlaceService.updateBusinessEmail({
        payload: e.payload,
        id: e.id
      }).catch(() => {
        this.showMarketplaceError(this.$t('pages.settings.marketplace_settings.errors.email.title'),
            this.$t('pages.settings.marketplace_settings.errors.email.text'),
            'error')
      })
    },
    popupFix(e) {
      const newWindow = e.newWindow
      if (newWindow) {
        this.handleLoginStart(newWindow, e)
      }
    },
    handleLoginStart(newWindow, e) {
      this.selectedRegion = e.region
      this.showError = false
      const url = `${location.origin}/lwa-redirect?currentLwaUri=${e.url}`

      const that = this
      that.showAuthWindow(newWindow, {
        url,
        mobile: that.$store.state.app.isMobile,
        callback: function () {
          if (!that.amazonKey) {
            that.showMarketplaceError(that.$t('pages.settings.marketplace_settings.errors.connection.title'),
                that.$t('pages.settings.marketplace_settings.errors.connection.text'),
                'error')
            that.setLoading(that.selectedRegion, false)

            UserActivityService.log('User closed marketplace modal or amazon error.')

          } else {
            const payload = {
              selling_partner_id: that.amazonKey.searchParams.get('selling_partner_id'),
              spapi_oauth_code: that.amazonKey.searchParams.get('spapi_oauth_code'),
              state: that.amazonKey.searchParams.get('state')
            };
            that.handleLwa(payload)
          }
        }
      })
    },
    showAuthWindow(newWindow, options) {
      newWindow.location.href = options.url

      const oauthInterval = setInterval(() => {

        try {
          if (newWindow.location.origin === newWindow.location.origin) {
            const decodedUrl = decodeURI(newWindow.location.href)
            const uri = new URL(decodedUrl)
            if (uri.searchParams.get('spapi_oauth_code')) {
              this.amazonKey = uri
            }
          }
        } catch (e) {
          // pass
        }

        if (newWindow.closed) {
          options.callback();
          clearInterval(oauthInterval);
        }
      }, 1000);
    },
    startBroadcast() {
      window.Echo.private(`user-${this.user.id}`).listen('.marketplace.refresh', () => {
        this.getMarketPlaces()
        this.$auth.fetch({})
        this.$store.dispatch('quota/getSubscriptionQuota')
      })
    },
    stopBroadcast() {
      window.Echo.private(`user-${this.user.id}`).stopListening('.marketplace.refresh')
    },
    setRegion(region) {
      switch (true) {
        case region === 'na':
          this.region = 0
          break
        case region === 'eu':
          this.region = 1
          break
        case region === 'fe':
          this.region = 2
          break
      }
    },
    async getMarketPlaces() {
      this.loading = true
      await UserMarketPlaceService.all({}).then(res => {
        res.data.data.forEach((item, index) => {
          this.setLoading(item.region, false)
          if (index === 0) {
            this.setRegion(item.region)
          }

          this.disabledRegions = Object.keys(res.data.regions)
          const preventDuplicate = this.regions[item.region].data.findIndex(market => market.marketplace_id === item.marketplace_id)
          if (this.regions[item.region]) {
            if (preventDuplicate > -1) {
              this.regions[item.region].data.splice(preventDuplicate, 1, item)
            } else {
              this.regions[item.region].data.push(item)
            }

            this.regions[item.region].info = res.data.regions[item.region]
          }

        })
      }).finally(() => {
        this.loading = false
      })
    }
  },
  mounted() {
    this.handleMarketplaceTour()
    const loading = ls.get('marketplace_loading')
    if (loading?.region) {
      this.regions[loading.region].loading = true
    }
  },
  created() {
    this.$auth.load().then(() => {
      this.user = this.$auth.user()
      this.startBroadcast()
    })
    this.getMarketPlaces()
  },
  beforeDestroy() {
    this.stopBroadcast()
  }
}
</script>
<style scoped>
/deep/ .v-tour__target--highlighted {
  box-shadow: 0 0 0 99999px rgba(0, 0, 0, .4);
}
</style>